import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useAuth } from '@/app/providers/authContext';
import { api, type ApiSchemas } from '@/lib/api';
import { queryKeys } from '@/lib/query';

/**
 * The boleto notices (`GET /v1/billing/notices`): boleto emitido, vence em 3 dias, vence hoje,
 * vencido — one per boleto, the latest. They are rendered by the in-app notification component
 * (the toast on entry and the bell) shared with the inactivity notices; this file only reads them
 * and closes or deletes them, so that component needs nothing boleto-specific beyond `stage`.
 */
export type BoletoNotice = ApiSchemas['BoletoNoticeRead'];
export type BoletoNoticeStage = BoletoNotice['stage'];

const NOTICES = '/v1/billing/notices';

/**
 * Each stage onto one of the four colours the notification component already has (the
 * inactivity proposal's: amber, red, graphite, green), so the bell gains no new colour.
 */
export const BOLETO_NOTICE_TONE: Readonly<
  Record<BoletoNoticeStage, 'amber' | 'red' | 'graphite' | 'green'>
> = {
  'boleto-issued': 'green',
  'boleto-due-soon': 'amber',
  'boleto-due-today': 'red',
  'boleto-overdue': 'red',
};

function useNoticesKey(): { key: readonly unknown[]; enabled: boolean } {
  const { tenantId } = useAuth();
  return {
    key: [...queryKeys.all(tenantId ?? 'sem-tenant'), 'billing-notices'],
    enabled: tenantId !== null,
  };
}

export function useBoletoNoticesQuery(): UseQueryResult<BoletoNotice[], Error> {
  const { key, enabled } = useNoticesKey();
  return useQuery({
    queryKey: key,
    queryFn: async ({ signal }) =>
      (await api.get<ApiSchemas['BoletoNoticeList']>(NOTICES, { signal })).items,
    enabled,
  });
}

/** The toast's ×: closed for today, still in the bell. */
export function useDismissBoletoNoticeMutation(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  const { key } = useNoticesKey();
  return useMutation({
    mutationFn: (id: string) => api.post<void>(`${NOTICES}/${encodeURIComponent(id)}/dismiss`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

/** The bell's ×, or "Excluir todas" when no id is given. */
export function useDeleteBoletoNoticeMutation(): UseMutationResult<void, Error, string | null> {
  const queryClient = useQueryClient();
  const { key } = useNoticesKey();
  return useMutation({
    mutationFn: (id: string | null) =>
      api.delete<void>(id === null ? NOTICES : `${NOTICES}/${encodeURIComponent(id)}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: key });
    },
  });
}
